import * as R from 'ramda';
import * as yup from 'yup';
import i18n from 'helpers/i18n';
import messages from './messages';
import useAsync from 'hooks/useAsync';
import { useMemo, useState, useEffect } from 'react';
import { useMeasure } from 'context/measure';
import { SectionCollapse } from 'components/SectionCollapse';
import { QuestionAccordion, QuestionAccordionItem } from './QuestionAccordion';
import { FeedbackTextArea } from './FeedbackTextArea';
import { getFocusedAgeLevel } from 'helpers/dimension/focusDimensions';
import {
  useValidations,
  useServerFieldError,
  getSubsectionStatus
} from 'components/Measure/View/ValidationsContext';
import { AgeLevel } from 'types/api/envScales/AgeLevel';
import { ObservationFeedback } from 'types/api/envScales/ObservationFeedback';

import {
  DomainGroup,
  getCycleColumns,
  getScoresByDomain
} from 'components/Measure/ObservationScores/ScoresByCycleTable/utils';

import { DimensionRangesTable } from 'components/Measure/ObservationScores/ScoresByCycleTable/DimensionRangesTable';

import {
  DimensionNote,
  DimensionNotesAccordion,
  buildDimensionNotes
} from 'components/Measure/ObservationNotes';

import {
  GetObservationFeedbackRes,
  getObservationFeedback,
  answerQuestion
} from 'actions/envScales/observationFeedback';

interface FeedbackSectionProps {
  observationId: number;
  targetType: TargetType;
  attached?: boolean;
}

type TargetType = 'dimensions' | 'domains';

const requiredText = yup.string().required();

// The server keys evidence.{domain,dimension}_feedback errors by t_name,
// not question_id.
function getTargetTName(
  ageLevel: AgeLevel,
  targetType: TargetType,
  targetId: number
): string | undefined {
  if (targetType === 'domains') {
    return ageLevel.domains.find(domain => domain.id === targetId)?.t_name;
  }

  for (const domain of ageLevel.domains) {
    const dimension = domain.dimensions.find(d => d.id === targetId);
    if (dimension) return dimension.t_name;
  }

  return undefined;
}

export function FeedbackSection({
  attached,
  observationId,
  targetType
}: FeedbackSectionProps) {
  const { assessment, ageLevel } = useMeasure();
  const { run, isPending } = useAsync();
  const { run: runAnswer } = useAsync();
  const { sections, errors, resolvedPaths, showErrorStatus } = useValidations();
  const [expandedQuestions, setExpandedQuestions] = useState<string[]>([]);
  const [questions, setQuestions] = useState<GetObservationFeedbackRes>({
    questions_with_answers: []
  });
  const allAnswered = questions.questions_with_answers.every(
    q => q.answer_notes?.length
  );
  const payloadType = targetType === 'dimensions' ? 'Dimension' : 'Domain';
  const subsectionKey =
    targetType === 'dimensions' ? 'dimension_feedback' : 'domain_feedback';

  const status = !showErrorStatus
    ? allAnswered
      ? 'completed'
      : 'pending'
    : sections
      ? getSubsectionStatus(sections.evidence, subsectionKey, showErrorStatus, {
          errors,
          resolvedPaths,
          prefix: `evidence.${subsectionKey}`
        })
      : 'pending';

  // Informal observations only show domains/dimensions in focus; formal is a
  // no-op here. Computed once and shared by both the scores table and notes.
  const focusedAgeLevel = useMemo(
    () => getFocusedAgeLevel(ageLevel, assessment),
    [ageLevel, assessment]
  );

  const cycles = useMemo(() => getCycleColumns(assessment), [assessment]);

  const domainGroups = useMemo(
    () => getScoresByDomain(focusedAgeLevel, assessment),
    [focusedAgeLevel, assessment]
  );

  const dimensionNotes = useMemo(
    () => buildDimensionNotes(focusedAgeLevel, assessment),
    [focusedAgeLevel, assessment]
  );

  useEffect(() => {
    run(getObservationFeedback(observationId, payloadType), {
      onSuccess: data => setQuestions(data)
    });
  }, [run, observationId, payloadType]);

  function updateQuestionsState(questionId: number, answer: string) {
    setQuestions(prevState => {
      const newState = prevState.questions_with_answers.map(obj =>
        obj.question_id === questionId ? { ...obj, answer_notes: answer } : obj
      );
      return { questions_with_answers: newState };
    });
  }

  function handleAnswer(answer: string, questionId: number) {
    const payload = { notes: answer, target_type: payloadType };
    runAnswer(answerQuestion(observationId, questionId, payload));
  }

  /**
   * The env-scales `question_id` for a Dimension/Domain feedback item is the
   * same id as `dimension.id`/`domain.id` in the age-level catalog. Scoped to
   * this one function so a future explicit `dimension_id`/`domain_id` field
   * from that API only requires a change here.
   */
  function getTargetId(record: { question_id: number }): number {
    return record.question_id;
  }

  /**
   * The domain group backing a feedback item's scores/notes: as-is for a
   * domain (already focus-filtered to in-focus dimensions), or narrowed to
   * just the one dimension for a dimension-level item. `undefined` when the
   * target isn't in the focused set at all (an informal observation's
   * domain/dimension entirely out of focus) -- nothing extra renders then.
   */
  function getDomainGroupForTarget(targetId: number): DomainGroup | undefined {
    if (targetType === 'domains') {
      return domainGroups.find(domain => domain.id === targetId);
    }

    const domain = domainGroups.find(domain =>
      domain.dimensions.some(dimension => dimension.id === targetId)
    );
    if (!domain) return undefined;

    const dimension = domain.dimensions.find(d => d.id === targetId);
    if (!dimension) return undefined;

    return { ...domain, dimensions: [dimension] };
  }

  function getNotesForTarget(domainGroup: DomainGroup): DimensionNote[] {
    const dimensionIds = new Set(domainGroup.dimensions.map(d => d.id));
    return dimensionNotes.filter(note => dimensionIds.has(note.id));
  }

  if (R.isEmpty(questions.questions_with_answers)) {
    return null;
  }

  return (
    <SectionCollapse
      title={i18n.ft(messages.titleFeedback)}
      subtitle={i18n.ft(messages.subtitle[targetType])}
      status={status}
      attached={attached}
      closeOthersOnOpen
    >
      {!isPending && (
        <QuestionAccordion
          value={expandedQuestions}
          onValueChange={setExpandedQuestions}
        >
          {questions.questions_with_answers.map(record => {
            const domainGroup = getDomainGroupForTarget(getTargetId(record));
            const tName = getTargetTName(
              ageLevel,
              targetType,
              getTargetId(record)
            );

            return (
              <FeedbackQuestionItem
                key={record.question_id}
                record={record}
                subsectionKey={subsectionKey}
                tName={tName}
                domainGroup={domainGroup}
                cycles={cycles}
                isInformal={assessment.informal}
                notes={domainGroup ? getNotesForTarget(domainGroup) : []}
                onAnswerChange={updateQuestionsState}
                onAnswerBlur={handleAnswer}
              />
            );
          })}
        </QuestionAccordion>
      )}
    </SectionCollapse>
  );
}

interface FeedbackQuestionItemProps {
  record: ObservationFeedback;
  subsectionKey: 'dimension_feedback' | 'domain_feedback';
  tName: string | undefined;
  domainGroup: DomainGroup | undefined;
  cycles: ReturnType<typeof getCycleColumns>;
  isInformal: boolean;
  notes: DimensionNote[];
  onAnswerChange: (questionId: number, answer: string) => void;
  onAnswerBlur: (answer: string, questionId: number) => void;
}

function FeedbackQuestionItem({
  record,
  subsectionKey,
  tName,
  domainGroup,
  cycles,
  isInformal,
  notes,
  onAnswerChange,
  onAnswerBlur
}: FeedbackQuestionItemProps) {
  const { showErrorStatus } = useValidations();

  // tName is only missing for a target outside the age-level catalog --
  // the fallback path then simply never matches a server error key.
  const path = `evidence.${subsectionKey}.${tName ?? record.question_id}`;
  const error = useServerFieldError(path, record.answer_notes, requiredText);

  // Live check instead of relying on `error` alone, which only narrows an
  // already-known error and won't catch a field cleared after being valid.
  const hasError =
    showErrorStatus && !requiredText.isValidSync(record.answer_notes);

  return (
    <QuestionAccordionItem
      questionId={record.question_id}
      question={record.question}
      isAnswered={Boolean(record.answer_notes?.length)}
      headingClassName="text-[18px] font-semibold leading-[26px]"
      contentClassName="pt-6"
      error={hasError}
    >
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <FeedbackTextArea
          label={record.question}
          dataQuestionId={record.question_id}
          value={record.answer_notes || ''}
          error={error?.message}
          onChange={answer => onAnswerChange(record.question_id, answer)}
          onBlur={() =>
            onAnswerBlur(record.answer_notes || '', record.question_id)
          }
        />

        {domainGroup && (
          <div className="flex flex-col gap-4">
            <DimensionRangesTable
              domainGroup={domainGroup}
              cycles={cycles}
              isInformal={isInformal}
            />

            <div>
              <h3 className="pb-2 font-inter text-ds-xs font-medium text-ds-text-secondary">
                {i18n.ft(messages.evidenceNotesTitle)}
              </h3>
              <DimensionNotesAccordion dimensionNotes={notes} />
            </div>
          </div>
        )}
      </div>
    </QuestionAccordionItem>
  );
}
